import axios from 'axios';

const LOCAL_API_BASE = 'http://localhost:3000/api/brandon-ui';
const PROD_API_BASE = process.env.REACT_APP_DEFAULT_REMOTE_API?.replace(/\/$/, '') || 'https://app.noderiver.ai/api/brandon-ui';

const isLocalHost = (hostname = '') =>
  hostname === 'localhost' ||
  hostname === '127.0.0.1' ||
  hostname.endsWith('.local');

const resolveDefaultApi = () => {
  if (typeof window !== 'undefined') {
    const { origin, hostname, port } = window.location;
    if (isLocalHost(hostname)) {
      if (port === '3001') {
        return LOCAL_API_BASE;
      }
      return `${origin.replace(/\/$/, '')}/api/brandon-ui`;
    }
    return PROD_API_BASE;
  }

  if (process.env.NODE_ENV === 'development') {
    return LOCAL_API_BASE;
  }
  return PROD_API_BASE;
};

const API = (process.env.REACT_APP_API_URL ?? resolveDefaultApi()).replace(/\/$/, '');

const resolveAuthBase = () => {
  try {
    return new URL(API).origin;
  } catch (error) {
    if (typeof window !== 'undefined' && window.location?.origin) {
      return window.location.origin;
    }
    return 'https://app.noderiver.ai';
  }
};

export const AUTH_BASE = resolveAuthBase();
axios.defaults.withCredentials = true;

const logRequest = (method, url, payload) => {
  console.debug(`[BrandOnUI][request] ${method.toUpperCase()} ${url}`, payload ?? "");
};

const logSuccess = (method, url, data) => {
  console.debug(`[BrandOnUI][response] ${method.toUpperCase()} ${url}`, data);
};

const logError = (method, url, error) => {
  console.error(`[BrandOnUI][error] ${method.toUpperCase()} ${url}:`, error?.response?.data ?? error?.message ?? error);
};

const buildQueryString = (params = {}) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    search.set(key, String(value));
  });
  const queryString = search.toString();
  return queryString ? `?${queryString}` : "";
};

// Brand API
export const getBrands = async (options = {}) => {
  const queryString = buildQueryString({ environment: options.environment });
  const url = `${API}/brands${queryString}`;
  logRequest("get", url);
  try {
    const response = await axios.get(url);
    logSuccess("get", url, response.data);
    return response.data;
  } catch (error) {
    logError("get", url, error);
    throw error;
  }
};

export const getBrand = async (brandId, options = {}) => {
  const queryString = buildQueryString({ environment: options.environment });
  const url = `${API}/brands/${brandId}${queryString}`;
  logRequest("get", url);
  try {
    const response = await axios.get(url);
    logSuccess("get", url, response.data);
    return response.data;
  } catch (error) {
    logError("get", url, error);
    throw error;
  }
};

// Conversation API
export const getConversations = async (brandId = null) => {
  const url = brandId ? `${API}/conversations?brand_id=${brandId}` : `${API}/conversations`;
  logRequest("get", url);
  try {
    const response = await axios.get(url);
    logSuccess("get", url, response.data);
    return response.data;
  } catch (error) {
    logError("get", url, error);
    throw error;
  }
};

export const getConversation = async (conversationId) => {
  const url = `${API}/conversations/${conversationId}`;
  logRequest("get", url);
  try {
    const response = await axios.get(url);
    logSuccess("get", url, response.data);
    return response.data;
  } catch (error) {
    logError("get", url, error);
    throw error;
  }
};

export const createConversation = async (brandId, title = 'New Conversation') => {
  const url = `${API}/conversations`;
  const payload = { brandId, title };
  logRequest("post", url, payload);
  try {
    const response = await axios.post(url, payload);
    logSuccess("post", url, response.data);
    return response.data;
  } catch (error) {
    logError("post", url, error);
    throw error;
  }
};

export const deleteConversation = async (conversationId) => {
  const url = `${API}/conversations/${conversationId}`;
  logRequest("delete", url);
  try {
    const response = await axios.delete(url);
    logSuccess("delete", url, response.data);
    return response.data;
  } catch (error) {
    logError("delete", url, error);
    throw error;
  }
};

export const updateConversation = async (conversationId, payload) => {
  const url = `${API}/conversations/${conversationId}`;
  logRequest("put", url, payload);
  try {
    const response = await axios.put(url, payload);
    logSuccess("put", url, response.data);
    return response.data;
  } catch (error) {
    logError("put", url, error);
    throw error;
  }
};

/**
 * Create a branch from a specific message in a conversation
 * @param {string} conversationId - The conversation ID to branch from
 * @param {string} messageId - The message ID to branch from (conversation will be duplicated up to this message)
 * @returns {Promise<Object>} The new branched conversation
 */
export const branchConversation = async (conversationId, messageId) => {
  const url = `${API}/conversations/${conversationId}/branch`;
  const payload = { messageId };
  logRequest("post", url, payload);
  try {
    const response = await axios.post(url, payload);
    logSuccess("post", url, response.data);
    return response.data;
  } catch (error) {
    logError("post", url, error);
    throw error;
  }
};

/**
 * Get the branch tree for a conversation family
 * Returns the full tree structure including all branches and their relationships
 * @param {string} conversationId - Any conversation ID in the branch family
 * @returns {Promise<Object>} The branch tree with nodes and relationships
 */
export const getConversationTree = async (conversationId) => {
  const url = `${API}/conversations/${conversationId}/tree`;
  logRequest("get", url);
  try {
    const response = await axios.get(url);
    logSuccess("get", url, response.data);
    return response.data;
  } catch (error) {
    logError("get", url, error);
    throw error;
  }
};

// Message API
export const sendMessage = async (conversationId, content) => {
  const url = `${API}/conversations/${conversationId}/messages`;
  const payload = { content };
  logRequest("post", url, payload);
  try {
    const response = await axios.post(url, payload);
    logSuccess("post", url, response.data);
    return response.data;
  } catch (error) {
    logError("post", url, error);
    throw error;
  }
};

/**
 * Send a message with streaming response
 * @param {string} conversationId - The conversation ID
 * @param {string} content - The message content
 * @param {Object} callbacks - Callback functions for streaming events
 * @param {Function} callbacks.onUserMessage - Called when user message is saved
 * @param {Function} callbacks.onToken - Called for each token received
 * @param {Function} callbacks.onDone - Called when streaming is complete with final message
 * @param {Function} callbacks.onError - Called on error
 * @returns {Promise<void>}
 */
export const sendMessageStream = async (conversationId, content, callbacks = {}) => {
  const url = `${API}/conversations/${conversationId}/messages/stream`;
  const payload = { content };
  logRequest("post", url, payload);
  
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Stream request failed' }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    // Check if this is a JSON response (clarification needed) vs SSE stream
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      // Server returned JSON instead of stream - handle clarification
      const jsonData = await response.json();
      logSuccess("post", url, jsonData);
      
      if (callbacks.onUserMessage && jsonData.user_message) {
        callbacks.onUserMessage(jsonData.user_message);
      }
      if (callbacks.onDone && jsonData.assistant_message) {
        callbacks.onDone(jsonData.assistant_message, jsonData.evaluation);
      }
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep incomplete line in buffer

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6).trim();
          if (!data) continue;

          try {
            const parsed = JSON.parse(data);
            
            if (parsed.type === 'user_message' && callbacks.onUserMessage) {
              callbacks.onUserMessage(parsed.message);
            } else if (parsed.type === 'token' && callbacks.onToken) {
              callbacks.onToken(parsed.content);
            } else if (parsed.type === 'done' && callbacks.onDone) {
              logSuccess("post", url, parsed);
              callbacks.onDone(parsed.message, parsed.evaluation);
            } else if (parsed.type === 'error' && callbacks.onError) {
              callbacks.onError(new Error(parsed.error));
            }
          } catch (parseError) {
            console.warn('[BrandOnUI] Failed to parse SSE data:', data, parseError);
          }
        }
      }
    }

    // Process any remaining data in buffer
    if (buffer.startsWith('data: ')) {
      const data = buffer.slice(6).trim();
      if (data) {
        try {
          const parsed = JSON.parse(data);
          if (parsed.type === 'done' && callbacks.onDone) {
            logSuccess("post", url, parsed);
            callbacks.onDone(parsed.message, parsed.evaluation);
          }
        } catch (parseError) {
          console.warn('[BrandOnUI] Failed to parse final SSE data:', data, parseError);
        }
      }
    }
  } catch (error) {
    logError("post", url, error);
    if (callbacks.onError) {
      callbacks.onError(error);
    }
    throw error;
  }
};

// Session + Auth helpers
export const getSessionInfo = async () => {
  const url = `${API}/session`;
  logRequest("get", url);
  try {
    const response = await axios.get(url);
    logSuccess("get", url, response.data);
    return response.data;
  } catch (error) {
    logError("get", url, error);
    throw error;
  }
};

export const logoutSession = async () => {
  const url = `${API}/logout`;
  logRequest("post", url);
  try {
    const response = await axios.post(url);
    logSuccess("post", url, response.data);
    return response.data;
  } catch (error) {
    logError("post", url, error);
    throw error;
  }
};

export const buildAuthLoginUrl = (callbackUrl) =>
  `${AUTH_BASE}/login?callbackUrl=${encodeURIComponent(callbackUrl)}`;

export const buildAuthSignupUrl = (callbackUrl) =>
  `${AUTH_BASE}/signup?callbackUrl=${encodeURIComponent(callbackUrl)}`;

export const getAuthBaseUrl = () => AUTH_BASE;

const maskEmail = (email) => {
  if (!email) return null;
  const [local, domain] = email.split('@');
  if (!local || !domain) return email;
  if (local.length <= 2) {
    return `${local[0] ?? ''}***@${domain}`;
  }
  return `${local.slice(0, 2)}***@${domain}`;
};

export const getAuthProvidersInfo = async () => {
  const url = `${API}/auth/providers`;
  logRequest("get", url);
  try {
    const response = await axios.get(url);
    logSuccess("get", url, response.data);
    return response.data;
  } catch (error) {
    logError("get", url, error);
    throw error;
  }
};

export const requestMagicLink = async ({ email, callbackUrl }) => {
  const url = `${API}/auth/magic-link`;
  const payload = { email, callbackUrl };
  logRequest("post", url, { ...payload, email: maskEmail(email) });
  try {
    const response = await axios.post(url, payload);
    logSuccess("post", url, response.data);
    return response.data;
  } catch (error) {
    logError("post", url, error);
    throw error;
  }
};

export const getCsrfToken = async () => {
  const url = `${AUTH_BASE}/api/auth/csrf`;
  logRequest("get", url);
  try {
    const response = await axios.get(url);
    logSuccess("get", url, response.data);
    return response.data?.csrfToken;
  } catch (error) {
    logError("get", url, error);
    return null;
  }
};

// Market Research API
export const generateIdentities = async ({ conversationId, segmentOverride, count, contentContext }) => {
  const url = `${API}/market-research/identities`;
  const payload = { conversationId, segmentOverride, count, contentContext };
  logRequest("post", url, payload);
  try {
    const response = await axios.post(url, payload);
    logSuccess("post", url, response.data);
    return response.data;
  } catch (error) {
    logError("post", url, error);
    throw error;
  }
};

export const conductPoll = async ({ content, identity }) => {
  const url = `${API}/market-research/poll`;
  const payload = { content, identity };
  logRequest("post", url, payload);
  try {
    const response = await axios.post(url, payload);
    logSuccess("post", url, response.data);
    return response.data;
  } catch (error) {
    logError("post", url, error);
    throw error;
  }
};

export const getMarketResearchSessions = async (brandId) => {
  const url = `${API}/market-research/sessions?brandId=${brandId}`;
  logRequest("get", url);
  try {
    const response = await axios.get(url);
    logSuccess("get", url, response.data);
    return response.data.sessions;
  } catch (error) {
    logError("get", url, error);
    throw error;
  }
};

export const getMarketResearchSession = async (sessionId) => {
  const url = `${API}/market-research/sessions/${sessionId}`;
  logRequest("get", url);
  try {
    const response = await axios.get(url);
    logSuccess("get", url, response.data);
    return response.data.session;
  } catch (error) {
    logError("get", url, error);
    throw error;
  }
};

export const updateProfile = async (payload) => {
  const url = `${API}/profile`;
  logRequest("put", url, payload);
  try {
    const response = await axios.put(url, payload);
    logSuccess("put", url, response.data);
    return response.data;
  } catch (error) {
    logError("put", url, error);
    throw error;
  }
};
